import Table, { TableView } from "@tiptap/extension-table"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import {
	addColumn,
	addRow,
	CellSelection,
	columnResizingPluginKey,
	TableMap,
} from "@tiptap/pm/tables"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

import type { Editor } from "@tiptap/core"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { Transaction } from "@tiptap/pm/state"
import type { TableRect } from "@tiptap/pm/tables"
import type { EditorView } from "@tiptap/pm/view"

// Matches min-w-[4rem] on table cells in shared.css, so a drag can't store a
// width narrower than the cell will ever render.
export const TABLE_CELL_MIN_WIDTH = 64

function plusButton(
	className: string,
	label: string,
	onClick: () => void,
): HTMLButtonElement {
	const button = document.createElement("button")
	button.type = "button"
	button.className = className
	button.setAttribute("aria-label", label)
	button.contentEditable = "false"
	button.textContent = "+"
	button.addEventListener("mousedown", (event) => {
		event.preventDefault()
		event.stopPropagation()
		onClick()
	})
	return button
}

function createTableView(editor: Editor) {
	return class TableViewWithControls extends TableView {
		wrapper: HTMLElement

		constructor(node: ProseMirrorNode, cellMinWidth: number) {
			super(node, cellMinWidth)

			this.wrapper = this.dom

			const outer = document.createElement("div")
			outer.className = "tableNode"
			outer.appendChild(this.wrapper)

			if (editor.isEditable) {
				outer.appendChild(
					plusButton("table-controls-add-column", "Add column", () =>
						this.run("column"),
					),
				)
				outer.appendChild(
					plusButton("table-controls-add-row", "Add row", () =>
						this.run("row"),
					),
				)
			}

			this.dom = outer
		}

		ignoreMutation(mutation: MutationRecord | { type: "selection" }) {
			if (mutation.type === "selection") return false
			const { target } = mutation
			if (this.contentDOM.contains(target) || target === this.contentDOM) {
				return super.ignoreMutation(mutation)
			}
			return true
		}

		private run(kind: "column" | "row") {
			const view = editor.view
			const pos = view.posAtDOM(this.dom, 0)
			if (pos == null || pos < 0) return
			const $pos = view.state.doc.resolve(pos)
			let tablePos = -1
			let tableNode: ProseMirrorNode | null = null
			for (let depth = $pos.depth; depth > 0; depth--) {
				const ancestor = $pos.node(depth)
				if (ancestor.type.name === "table") {
					tableNode = ancestor
					tablePos = $pos.before(depth)
					break
				}
			}
			if (!tableNode || tablePos < 0) return
			const map = TableMap.get(tableNode)
			const rect = {
				map,
				tableStart: tablePos + 1,
				table: tableNode,
			} as unknown as TableRect
			const tr =
				kind === "column"
					? addColumn(view.state.tr, rect, map.width)
					: addRow(view.state.tr, rect, map.height)
			view.dispatch(tr)
			view.focus()
		}
	}
}

export function storeRenderedColumnWidths(
	tr: Transaction,
	tablePos: number,
	renderedWidths: readonly (number | null)[],
): Transaction {
	const table = tr.doc.nodeAt(tablePos)
	if (!table) return tr
	const map = TableMap.get(table)
	const seen = new Set<number>()
	for (const cellPos of map.map) {
		if (seen.has(cellPos)) continue
		seen.add(cellPos)
		const cell = table.nodeAt(cellPos)
		const colwidth = cell?.attrs.colwidth as (number | null)[] | null
		if (!cell || !colwidth) continue
		const firstColumn = map.colCount(cellPos)
		let changed = false
		const next = colwidth.map((width, offset) => {
			const rendered = renderedWidths[firstColumn + offset]
			if (!width || rendered == null || Math.abs(rendered - width) <= 1) {
				return width
			}
			changed = true
			return rendered
		})
		if (changed) {
			tr.setNodeMarkup(tablePos + 1 + cellPos, null, {
				...cell.attrs,
				colwidth: next,
			})
		}
	}
	return tr
}

function measureColumnWidths(
	view: EditorView,
	tablePos: number,
	table: ProseMirrorNode,
): (number | null)[] {
	const map = TableMap.get(table)
	const widths: (number | null)[] = new Array<number | null>(map.width).fill(
		null,
	)
	for (let index = 0; index < map.map.length; index++) {
		const column = index % map.width
		if (widths[column] != null) continue
		const cellPos = map.map[index]
		if (cellPos == null || table.nodeAt(cellPos)?.attrs.colspan !== 1) continue
		const dom = view.nodeDOM(tablePos + 1 + cellPos)
		if (dom instanceof HTMLElement) widths[column] = dom.offsetWidth
	}
	return widths
}

// The table shrinks to fit the column, so a stored width can be wider than the
// cell on screen. prosemirror-tables starts a drag from the stored value, which
// leaves the border trailing the cursor; store what's shown before it reads it.
const fitColumnWidthsKey = new PluginKey("tableFitColumnWidths")

function fitColumnWidthsPlugin() {
	return new Plugin({
		key: fitColumnWidthsKey,
		props: {
			handleDOMEvents: {
				mousedown(view) {
					const resize = columnResizingPluginKey.getState(view.state)
					if (
						!view.editable ||
						!resize ||
						resize.activeHandle === -1 ||
						resize.dragging
					) {
						return false
					}
					const $cell = view.state.doc.resolve(resize.activeHandle)
					const tablePos = $cell.before(-1)
					const tr = storeRenderedColumnWidths(
						view.state.tr,
						tablePos,
						measureColumnWidths(view, tablePos, $cell.node(-1)),
					)
					if (tr.docChanged) view.dispatch(tr.setMeta("addToHistory", false))
					return false
				},
			},
		},
	})
}

const cellSelectionBorderKey = new PluginKey("cellSelectionBorder")

function cellSelectionDecorations(state: {
	selection: unknown
	doc: ProseMirrorNode
}): DecorationSet {
	const selection = state.selection
	if (!(selection instanceof CellSelection)) {
		return DecorationSet.empty
	}

	const $anchor = selection.$anchorCell
	const table = $anchor.node(-1)
	const tableStart = $anchor.start(-1)
	const map = TableMap.get(table)
	const rect = map.rectBetween(
		selection.$anchorCell.pos - tableStart,
		selection.$headCell.pos - tableStart,
	)

	const decorations: Decoration[] = []
	for (let row = rect.top; row < rect.bottom; row++) {
		for (let col = rect.left; col < rect.right; col++) {
			const cellPos = map.map[row * map.width + col]
			if (cellPos == null) continue
			const classes = ["pl-cell-selected"]
			if (row === rect.top) classes.push("pl-cell-selected-top")
			if (row === rect.bottom - 1) classes.push("pl-cell-selected-bottom")
			if (col === rect.left) classes.push("pl-cell-selected-left")
			if (col === rect.right - 1) classes.push("pl-cell-selected-right")
			const from = tableStart + cellPos
			const cellNode = table.nodeAt(cellPos)
			if (!cellNode) continue
			decorations.push(
				Decoration.node(from, from + cellNode.nodeSize, {
					class: classes.join(" "),
				}),
			)
		}
	}

	return DecorationSet.create(state.doc, decorations)
}

export const TableWithControls = Table.extend({
	addOptions() {
		return {
			...this.parent?.(),
			cellMinWidth: TABLE_CELL_MIN_WIDTH,
		}
	},

	onBeforeCreate() {
		this.options.View = createTableView(this.editor)
	},

	addProseMirrorPlugins() {
		const plugins = this.parent?.() ?? []
		return [
			// Before columnResizing, whose mousedown reads the stored width.
			fitColumnWidthsPlugin(),
			...plugins,
			new Plugin({
				key: cellSelectionBorderKey,
				props: {
					decorations(state) {
						return cellSelectionDecorations(state)
					},
				},
			}),
		]
	},
})

export default TableWithControls
